import { Component, OnInit, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Subscription } from 'rxjs';
import { MoneyService } from '../money.service';
import {
  Rule, StagedTxn, BalancePoint, TxnSource, Category, ImportRecord, Txn, Account,
  defaultCutoffDate,
} from '../money.model';
import { parseCsv } from '../csv/csv-parser';
import { detectSource, APPLE_ACCOUNT_ID } from '../csv/detect-source';
import { adaptAppleCard } from '../csv/apple-card.adapter';
import { adaptBank } from '../csv/bank.adapter';
import { fingerprintTxns } from '../csv/fingerprint';
import { applyRules } from '../rules/rules.engine';
import { buildCommitChunks } from '../csv/commit-payload';

export interface StagedFile {
  filename: string;
  source: TxnSource | null;
  txns: StagedTxn[];
  balances: BalancePoint[];
  rowCount: number;
  newCount: number;
  duplicateCount: number;
  pendingCount: number;
  skippedOldCount: number;
  dateFrom: string;
  dateTo: string;
  accountId: string;
  accountMask: string;
  error: string;
  /** Raw text this file was staged from, so restage() and discard() never desync from `staged`. */
  rawText: string;
}

const empty = (filename: string, error: string, rawText: string): StagedFile => ({
  filename, source: null, txns: [], balances: [],
  rowCount: 0, newCount: 0, duplicateCount: 0, pendingCount: 0, skippedOldCount: 0,
  dateFrom: '', dateTo: '', accountId: '', accountMask: '', error, rawText,
});

/**
 * Pure. Runs the whole pipeline for one file and returns everything the preview
 * needs. Nothing is written — the preview is a hard confirm gate.
 *
 * `cutoffDate` is an ISO date; rows before it are dropped along with their balance
 * points, and counted. An empty string means no cutoff. Dropping happens BEFORE
 * fingerprinting so a trimmed row is never mistaken for a duplicate.
 */
export function stageFile(
  text: string, filename: string, rules: Rule[], knownFingerprints: Set<string>,
  cutoffDate: string,
): StagedFile {
  let rows: string[][];
  try { rows = parseCsv(text); }
  catch (e) { return empty(filename, `Could not read ${filename}: ${(e as Error).message}`, text); }

  if (rows.length < 2) return empty(filename, `${filename} has no data rows.`, text);

  const source = detectSource(rows[0]);
  if (!source) {
    return empty(filename,
      `Unrecognized CSV. Found: [${rows[0].join(', ')}] — expected an Apple Card or bank export.`, text);
  }

  let result;
  try { result = source === 'apple-card' ? adaptAppleCard(rows) : adaptBank(rows); }
  catch (e) { return empty(filename, `Could not read ${filename}: ${(e as Error).message}`, text); }

  // Cutoff BEFORE fingerprinting, so a trimmed row is never counted as a duplicate.
  const kept = cutoffDate
    ? result.txns.filter(t => t.date >= cutoffDate)
    : result.txns;
  const skippedOldCount = result.txns.length - kept.length;

  const keptBalances = cutoffDate
    ? result.balances.filter(b => b.date >= cutoffDate)
    : result.balances;

  const staged = applyRules(fingerprintTxns(kept), rules, knownFingerprints);
  const dates = staged.map(t => t.date).sort();

  return {
    filename,
    source,
    txns: staged,
    balances: keptBalances,
    rowCount: rows.length - 1,
    newCount: staged.filter(t => !t.isDuplicate).length,
    duplicateCount: staged.filter(t => t.isDuplicate).length,
    pendingCount: result.pendingCount,
    skippedOldCount,
    dateFrom: dates[0] ?? '',
    dateTo: dates[dates.length - 1] ?? '',
    accountId: staged[0]?.accountId ?? (source === 'apple-card' ? APPLE_ACCOUNT_ID : ''),
    accountMask: result.accountMask,
    error: '',
    rawText: text,
  };
}

/**
 * Pure. Decides what actually gets written for a commit: rows the user hasn't
 * already been told are duplicates, AND rows not already sitting in `known` —
 * which may have been added there since the file was staged (e.g. a previous
 * commit attempt landed them before a later file in the same batch failed).
 * Re-checking against live fingerprints at commit time, not just stage time,
 * is what makes a retry after a partial failure idempotent.
 */
export function pendingForCommit(txns: StagedTxn[], known: Set<string>): StagedTxn[] {
  return txns.filter(t => !t.isDuplicate && !known.has(t.fingerprint));
}

@Component({
  selector: 'app-money-import',
  templateUrl: './import.component.html',
  styleUrls: ['./import.component.scss'],
  standalone: false,
})
export class ImportComponent implements OnInit, OnDestroy {
  staged: StagedFile[] = [];
  categories: Category[] = [];
  imports: ImportRecord[] = [];
  committing = false;
  progress = '';
  commitError = '';
  dragOver = false;

  /** Editable import window. Rows before this are skipped and counted. */
  cutoffDate = defaultCutoffDate(new Date());

  private uid = '';
  private rules: Rule[] = [];
  private txns: Txn[] = [];
  private subs: Subscription[] = [];

  constructor(private afAuth: AngularFireAuth, private money: MoneyService) {}

  ngOnInit(): void {
    this.subs.push(this.afAuth.authState.subscribe(user => {
      if (!user) return;
      this.uid = user.uid;
      this.subs.push(this.money.subscribeAll(this.uid).subscribe(data => {
        this.rules = data.rules;
        this.categories = data.categories;
        this.txns = data.txns;
        this.imports = [...data.imports].sort((a, b) => b.createdAt - a.createdAt);
      }));
    }));
  }

  // BOTH subscriptions: an unstored authState subscription outlives the
  // component and re-runs against a destroyed instance on the next auth event.
  ngOnDestroy(): void { this.subs.forEach(s => s.unsubscribe()); }

  trackStaged(_: number, file: StagedFile): string { return file.filename; }
  trackTxn(_: number, t: StagedTxn): string { return t.fingerprint; }
  trackCategory(_: number, c: Category): string { return c.id; }
  trackImport(_: number, rec: ImportRecord): string { return rec.id; }

  get totalNew(): number { return this.staged.reduce((n, s) => n + s.newCount, 0); }
  get hasErrors(): boolean { return this.staged.some(s => !!s.error); }

  onDragOver(e: DragEvent): void { e.preventDefault(); this.dragOver = true; }
  onDragLeave(): void { this.dragOver = false; }

  onDrop(e: DragEvent): void {
    e.preventDefault();
    this.dragOver = false;
    this.readFiles(Array.from(e.dataTransfer?.files ?? []));
  }

  onPick(e: Event): void {
    this.readFiles(Array.from((e.target as HTMLInputElement).files ?? []));
  }

  /**
   * Every template-invoked async handler routes through this so a rejected
   * write surfaces as a visible message instead of an unhandled rejection the
   * user never sees. Reuses `commitError` — same field commit() already sets.
   */
  private async safe(action: () => Promise<void>): Promise<void> {
    try {
      await action();
    } catch (e) {
      this.commitError = (e as Error).message;
    }
  }

  /** Reads locally via FileReader. No file is uploaded anywhere. */
  private async readFiles(files: File[]): Promise<void> {
    if (!this.uid) {
      // authState hasn't resolved yet. Without this guard getFingerprints
      // would query users//money/fingerprints, Firebase would reject, and the
      // dropzone would silently do nothing.
      this.commitError = 'Still signing in — try dropping the files again in a moment.';
      return;
    }
    await this.safe(async () => {
      const known = await this.money.getFingerprints(this.uid);
      const seenInBatch = new Set<string>(known);

      for (const file of files) {
        const text = await file.text();
        const result = stageFile(text, file.name, this.rules, seenInBatch, this.cutoffDate);
        // Later files in the same drop must see earlier files' rows as duplicates.
        result.txns.filter(t => !t.isDuplicate).forEach(t => seenInBatch.add(t.fingerprint));
        this.staged = [...this.staged, result];
      }
    });
  }

  /**
   * Changing the window after dropping files re-reads the files dropped so far
   * and re-stages them at the new cutoff. Any categorization already applied in
   * the preview is lost — the trade for that is dropped rows genuinely coming back.
   */
  async restage(): Promise<void> {
    if (!this.uid) {
      this.commitError = 'Still signing in — try changing the date again in a moment.';
      return;
    }
    await this.safe(async () => {
      const known = await this.money.getFingerprints(this.uid);
      const seenInBatch = new Set<string>(known);
      const results: StagedFile[] = [];

      for (const { name, text } of this.staged.map(s => ({ name: s.filename, text: s.rawText }))) {
        const result = stageFile(text, name, this.rules, seenInBatch, this.cutoffDate);
        result.txns.filter(t => !t.isDuplicate).forEach(t => seenInBatch.add(t.fingerprint));
        results.push(result);
      }

      this.staged = results;
    });
  }

  setCategory(txn: StagedTxn, categoryId: string): void {
    txn.categoryId = categoryId || null;
  }

  discard(index: number): void {
    this.staged = this.staged.filter((_, i) => i !== index);
  }

  async commit(): Promise<void> {
    if (this.committing || this.totalNew === 0) return;
    this.committing = true;
    this.commitError = '';

    try {
      // Live, not the stage-time snapshot: a retry after a partial failure must
      // see everything a prior attempt already landed, including files this
      // very loop is about to commit. Fetched inside this try so a failure here
      // (e.g. a network hiccup) is surfaced the same way a per-file failure is,
      // instead of becoming an unhandled rejection nobody sees.
      let known: Set<string>;
      try {
        known = await this.money.getFingerprints(this.uid);
      } catch (e) {
        this.commitError =
          `Could not check for duplicate transactions: ${(e as Error).message}. ` +
          `Nothing was imported — fix the issue and retry.`;
        return;
      }

      for (const file of this.staged) {
        if (file.error || file.newCount === 0) continue;

        try {
          const toCommit = pendingForCommit(file.txns, known);

          if (toCommit.length > 0) {
            const importId = `imp-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

            await this.money.upsertAccount(this.uid, {
              id: file.accountId,
              name: file.source === 'apple-card' ? 'Apple Card' : `Checking ••${file.accountMask}`,
              kind: file.source === 'apple-card' ? 'credit' : 'checking',
              source: file.source!,
              mask: file.accountMask,
              createdAt: Date.now(),
            } as Account);

            const chunks = buildCommitChunks({
              importId, filename: file.filename, source: file.source!,
              accountId: file.accountId, txns: toCommit, balances: file.balances,
              rowCount: file.rowCount, duplicateCount: file.duplicateCount,
              pendingCount: file.pendingCount, now: Date.now(),
            });

            await this.money.commitChunks(this.uid, chunks, (done, total) => {
              this.progress = `${file.filename}: ${done} of ${total} batches`;
            });

            toCommit.forEach(t => known.add(t.fingerprint));
          }

          // Drop this file the moment ITS commit lands, so a later failure
          // only leaves behind the files that genuinely didn't make it in.
          this.staged = this.staged.filter(f => f !== file);
        } catch (e) {
          this.commitError =
            `${file.filename} failed to import: ${(e as Error).message}. ` +
            `Already-imported files above have been cleared — fix the issue and retry to pick up the rest.`;
          return;
        }
      }

      this.staged = [];
      this.progress = 'Import complete.';
    } finally {
      this.committing = false;
    }
  }

  async undo(record: ImportRecord): Promise<void> {
    await this.safe(() => this.money.deleteImport(this.uid, record, this.txns));
  }
}
